import { createSelector } from '@reduxjs/toolkit';

import { ReportContext } from '@paragon/themes';

import { CURRENT_VERSION } from '../../components/header/OpenUserSettingsMenuItem';
import { RootState } from '..';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getProjectLayoutModel } from './getProjectLayoutModel';
import { getFirstSelectedItemEntryOrNull } from './getSelectedItemSelector';

/**
 * Names what the user has open, for whoever reads the report in Slack.
 *
 * The report already carries `window.location.href`, which `updateUrl` keeps current with the project, component
 * design, and selected item — but it's all GUIDs, so a support engineer scanning the channel learns nothing without
 * clicking through. This is the same information in names.
 */
const getWhatIsOpenDescription = createSelector(
  (state: RootState) => state.application.openContext,
  getProjectLayoutModel,
  getActiveComponentDesign,
  getFirstSelectedItemEntryOrNull,

  (openContext, project, activeComponentDesign, selectedEntry): string | undefined => {
    const parts: string[] = [];

    switch (openContext?.type) {
      case 'job':
        parts.push(`job ${openContext.jobId}`);
        break;

      case 'shared-component-set':
        parts.push(`shared set ${openContext.sharedComponentSetLookupCode}`);
        break;

      default:
        break;
    }

    if (project?.name) parts.push(project.name);
    if (activeComponentDesign?.component.name) parts.push(activeComponentDesign.component.name);
    if (selectedEntry) parts.push(`selected: ${selectedEntry.type}`);

    return parts.length > 0 ? parts.join(' · ') : undefined;
  },
);

/**
 * Describes the user's position in the Design client for a problem report — everything the themes library cannot work
 * out for itself, which is the names above plus which build they are on.
 */
export const getProblemReportContext = createSelector(
  getWhatIsOpenDescription,

  (description): ReportContext => ({
    description,
    clientVersion: `${CURRENT_VERSION}`,
  }),
);
